/* eslint-disable react-hooks/exhaustive-deps */
import Loading from "components/Loading";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaBriefcase } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  fetchHigherAdminRequest,
  fetchHigherAdminRequestForCoordinator,
  fetchHigherAdminRequestForProsecutor,
  fetchHigherAdminRequestForRegistrar,
} from "services/appealService";
import {
  HigherAdminFeedbackRequest,
  HigherAdminFeedbackRequestStatus,
  Roles,
} from "types";
import { errorHandler, hasRole } from "utils/helper";
import PageHeader from "../../components/PageHeader";

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const getStatusColor = (status: string) => {
    const statusMap: { [key: string]: string } = {
      submitted: "bg-yellow-100 text-yellow-800 border-yellow-200",
      assigned_to_prosecutor: "bg-red-100 text-red-800 border-red-200",
      assigned_to_clerk: "bg-blue-100 text-blue-800 border-blue-200",
      forwarded_to_coordinator:
        "bg-purple-100 text-purple-800 border-purple-200",
      completed: "bg-green-100 text-green-800 border-green-200",
    };
    return statusMap[status] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
        status,
      )}`}
    >
      {HigherAdminFeedbackRequestStatus[
        status as keyof typeof HigherAdminFeedbackRequestStatus
      ] || status}
    </span>
  );
};

const HigherAdminList: React.FC = () => {
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("Prosecutor Appeal");
  const [requests, setRequests] = useState<HigherAdminFeedbackRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const isVariousCoordinator = hasRole(loginData?.roles, [
    Roles.variousCoordinator,
    Roles.variousAppealCoordinator,
  ]);
  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);

  const fetchData = () => {
    setLoading(true);
    const api = isProsecutor
      ? fetchHigherAdminRequestForProsecutor(loginData?.user_id + "")
      : isRegistrar
        ? fetchHigherAdminRequestForRegistrar(loginData?.admin_unit + "")
        : isVariousCoordinator
          ? fetchHigherAdminRequestForCoordinator(
              loginData?.admin_unit + "",
              "NewVariousCaseCoordinator",
            )
          : fetchHigherAdminRequest();

    api
      .then((data) => {
        setRequests(data);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setLoading(false);
      });
  };

  useEffect(() => {
    if (loginData && activeTab) {
      fetchData();
    }
  }, [activeTab, loginData]);

  const searchTerm = search.trim().toLowerCase();
  const filtered = requests.filter((request) => {
    const requested_by = request?.requested_by?.toString()?.toLowerCase() || "";
    const assigned_prosecutor =
      request?.assigned_prosecutor?.toString()?.toLowerCase() || "";
    const status = request?.status?.toString()?.toLowerCase() || "";
    const crime_type = request?.crime_type?.toString()?.toLowerCase() || "";

    const matchesText =
      requested_by.includes(searchTerm) ||
      assigned_prosecutor.includes(searchTerm) ||
      status.includes(searchTerm) ||
      crime_type.includes(searchTerm);

    return matchesText;
  });

  return (
    <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
      <PageHeader
        title={t("higher_admin_feedback")}
        icon={FaBriefcase}
        activeTab={activeTab}
        onTabClick={setActiveTab}
        rightContent={<></>}
      />

      <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
        <div className="overflow-hidden bg-white shadow rounded-xl">
          {loading ? (
            <Loading label={t("loading_appeal")} />
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="text-white bg-black">
                <tr>
                  <th colSpan={7} className="p-4" rounded-t-lg>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-lg font-semibold">
                        <button
                          onClick={() => navigate(-1)}
                          className="text-white hover:text-gray-300"
                          title={t("go_back")}
                        >
                          ◀️
                        </button>
                        <span>{t("higher_admin_feedback_list_table")}</span>
                      </div>

                      <div className="relative w-80">
                        <span className="absolute inset-y-0 flex items-center text-gray-500 pointer-events-none left-3">
                          🔍
                        </span>
                        <input
                          type="text"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder={t("search")}
                          className="w-full py-2 pl-10 pr-4 text-sm text-black placeholder-gray-600 bg-gray-200 rounded-md focus:outline-none"
                        />
                      </div>
                    </div>
                  </th>
                </tr>
                <tr>
                  <th className="p-3 text-left">{t("crime_type")}</th>
                  <th className="p-3 text-left">{t("requested_by")}</th>
                  <th className="p-3 text-left">{t("assigned_prosecutor")}</th>
                  <th className="p-3 text-left">{t("status")}</th>
                  <th className="p-3 text-left" rounded-tr-lg>
                    {t("detail")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((request, idx) => (
                  <tr
                    key={idx}
                    className={idx % 2 === 0 ? "bg-[#F7F4F1AB]" : "bg-white"}
                  >
                    <td className="p-3 font-[500] font-[Poppins]">
                      {request?.crime_type ?? "-"}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {request?.requested_by ?? "-"}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {request?.assigned_prosecutor ?? "-"}
                    </td>
                    <td className="p-3 font-[500] font-[inter]">
                      <StatusBadge status={request?.status || ""} />
                    </td>

                    <td className="p-3 font-[500] font-[Poppins] flex flex-row gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          navigate(request?.id + "");
                        }}
                        className="px-4 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                      >
                        {t("view")}
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered?.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-4 text-center text-gray-500">
                      {t("no_appeal_found")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default HigherAdminList;
